import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Link as LinkIcon, RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const REFRESH_INTERVAL_MS = 60_000;

/**
 * URLRotWatchdogTile — status of the `wua-url-rot-watchdog` supervised
 * daemon. Probes every entry in `data/known_admissions_urls.py` daily
 * and surfaces stale seed URLs (e.g., Manchester's `/about/facts-figures/`
 * going 404) so we can patch them before students hit them.
 */
export default function URLRotWatchdogTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/url-rot/status");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load URL-rot status");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (err) {
    return (
      <div
        data-testid="url-rot-tile-error"
        className="border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900"
      >
        URL-rot watchdog tile failed: {err}
      </div>
    );
  }
  if (!data || !data.latest) {
    return (
      <div
        data-testid="url-rot-tile-loading"
        className="border border-stone-200 rounded-xl p-4 text-sm text-stone-500"
      >
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
        Waiting for first URL-rot pass…
      </div>
    );
  }

  const { latest, trend } = data;
  const allGreen = latest.fail_count === 0;

  return (
    <div
      data-testid="url-rot-tile"
      className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${allGreen ? "bg-emerald-50" : "bg-amber-50"}`}>
            <LinkIcon className={`h-5 w-5 ${allGreen ? "text-emerald-700" : "text-amber-700"}`} />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Seed URL rot watchdog
            </div>
            <div className="text-xs text-stone-500">
              Daemon: <code>wua-url-rot-watchdog</code> · last run{" "}
              {new Date(latest.run_at_iso).toLocaleString()}
            </div>
          </div>
        </div>
        <button
          data-testid="url-rot-refresh-btn"
          onClick={load}
          className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div
          data-testid="url-rot-summary-count"
          className="rounded-lg border border-stone-100 p-3 flex-1"
        >
          <div className="text-xs text-stone-500 mb-1">Green / total</div>
          <div className={`text-2xl font-semibold ${allGreen ? "text-emerald-700" : "text-amber-700"}`}>
            {latest.ok_count} / {latest.total}
          </div>
          <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${allGreen ? "bg-emerald-500" : "bg-amber-500"}`}
              style={{ width: `${(latest.ok_count / Math.max(1, latest.total)) * 100}%` }}
            />
          </div>
        </div>
        <div className="rounded-lg border border-stone-100 p-3 flex-1">
          <div className="text-xs text-stone-500 mb-1">Trend (last 7 passes)</div>
          <div className="flex items-end gap-0.5 h-10">
            {(trend || []).slice(-7).map((t, i) => {
              const pct = t.total ? (t.ok_count / t.total) * 100 : 0;
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-sm ${pct === 100 ? "bg-emerald-500" : "bg-amber-500"}`}
                  style={{ height: `${Math.max(6, pct)}%` }}
                  title={`${new Date(t.run_at_iso).toLocaleDateString()} — ${t.ok_count}/${t.total}`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {latest.failing && latest.failing.length > 0 ? (
        <div className="mt-4">
          <div className="text-xs text-stone-500 mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            {latest.failing.length} URL(s) failing — patch in
            <code className="text-stone-700">data/known_admissions_urls.py</code>
          </div>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {latest.failing.map((f, i) => (
              <li
                key={i}
                data-testid={`url-rot-failing-${f.uni_id}`}
                className="text-xs flex items-center gap-2 text-stone-700"
              >
                <span className="inline-block w-14 text-center rounded bg-rose-100 text-rose-800 text-[10px] font-medium py-0.5">
                  {f.status ?? "ERR"}
                </span>
                <span className="text-stone-500 w-24 truncate">{f.uni_id}</span>
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate flex-1 hover:underline"
                >
                  {f.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2 text-xs text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          All {latest.total} seed URLs resolve. Next check in 24h.
        </div>
      )}

      <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
        Runs every 24h through the Scrapfly ladder (httpx → curl_cffi
        → Scrapfly). Retains last 30 passes for the 7-day trend.
      </p>
    </div>
  );
}
